import * as THREE from 'three';

export type CookieShape = 'heart' | 'star' | 'circle' | 'flower' | 'cloud' | 'hexagon' | 'arch';

export interface CookieCutterParams {
  shape: CookieShape;
  size: number;                 // Diámetro / tamaño exterior en mm (ej. 75)
  bladeHeight: number;          // Altura de corte en mm (ej. 15)
  bladeThickness: number;       // Grosor de pared de cuchilla en mm (ej. 1.0)
  flangeWidth: number;          // Ancho de labio/pestaña de agarre en mm (ej. 4.5)
  flangeHeight: number;         // Altura del labio de agarre en mm (ej. 2.5)
  taperCuttingEdge: boolean;    // Filo cónico en la punta
  
  // Sello interior (Stamp)
  hasStamp: boolean;            // Incluir sello
  stampThickness: number;       // Altura de base del sello en mm (ej. 2.5)
  stampReliefHeight: number;    // Altura del dibujo/relieve en mm (ej. 1.8)
  stampHandleHeight: number;    // Altura de la manija de agarre en mm (ej. 14)
  stampHandleDiameter: number;  // Diámetro de la manija en mm (ej. 18)
  stampClearance: number;       // Tolerancia de holgura entre cortante y sello en mm (ej. 1.0)
}

/**
 * Genera el contorno 2D normalizado centrado [-0.5, 0.5] para cada forma
 */
export function getShapePoints(shape: CookieShape, pointsCount = 120): THREE.Vector2[] {
  const points: THREE.Vector2[] = [];

  for (let i = 0; i < pointsCount; i++) {
    const t = (i / pointsCount) * Math.PI * 2;
    let x = 0;
    let y = 0;

    switch (shape) {
      case 'circle':
        x = Math.cos(t) * 0.5;
        y = Math.sin(t) * 0.5;
        break;

      case 'heart':
        // Ecuación paramétrica de corazón cardioide
        x = 0.5 * (16 * Math.pow(Math.sin(t), 3)) / 16;
        y = -0.5 * (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) / 16;
        break;

      case 'star': {
        const spikes = 5;
        // Suavizado armónico
        const harmR = 0.365 + 0.135 * Math.cos(spikes * t);
        x = Math.cos(t) * harmR;
        y = Math.sin(t) * harmR;
        break;
      }

      case 'flower': {
        const petals = 6;
        const r = 0.38 + 0.12 * Math.cos(petals * t);
        x = Math.cos(t) * r;
        y = Math.sin(t) * r;
        break;
      }

      case 'cloud': {
        const r = 0.38 + 0.08 * Math.sin(3 * t) + 0.05 * Math.cos(7 * t);
        x = Math.cos(t) * r * 1.15;
        y = Math.sin(t) * r * 0.85;
        break;
      }

      case 'hexagon': {
        const sides = 6;
        const r = 0.48 / Math.cos(((t + Math.PI / sides) % (2 * Math.PI / sides)) - Math.PI / sides);
        x = Math.cos(t) * Math.min(0.5, r);
        y = Math.sin(t) * Math.min(0.5, r);
        break;
      }

      case 'arch': {
        // Arco japonés / Puerta Torii curvada
        const angle = t;
        if (angle >= 0 && angle <= Math.PI) {
          x = Math.cos(angle) * 0.45;
          y = Math.sin(angle) * 0.45;
        } else {
          const norm = (angle - Math.PI) / Math.PI;
          x = (norm * 2 - 1) * 0.45;
          y = -0.45;
        }
        break;
      }
    }

    points.push(new THREE.Vector2(x, y));
  }

  return points;
}

/**
 * Genera la geometría 3D del Cortante Perimetral (Blade + Flange de apoyo)
 */
export function generateCutterGeometry(params: CookieCutterParams): THREE.BufferGeometry {
  const {
    shape = 'heart',
    size = 75,
    bladeHeight = 15,
    bladeThickness = 1.0,
    flangeWidth = 4.5,
    flangeHeight = 2.5
  } = params;

  const basePoints = getShapePoints(shape, 128);
  const n = basePoints.length;

  // Escalar a tamaño real en mm
  const centerScale = size;
  const scaledPoints: THREE.Vector2[] = basePoints.map(p => new THREE.Vector2(p.x * centerScale, p.y * centerScale));

  // Calcular normales 2D perpendiculares para offset exterior e interior
  const normals: THREE.Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const prev = scaledPoints[(i - 1 + n) % n];
    const next = scaledPoints[(i + 1) % n];
    const tangent = new THREE.Vector2().subVectors(next, prev).normalize();
    // Normal hacia afuera (tangente rotada 90°)
    const normal = new THREE.Vector2(-tangent.y, tangent.x).normalize();
    normals.push(normal);
  }

  const positions: number[] = [];
  const indices: number[] = [];

  // Anillos de vértices:
  // Anillo 0: Base exterior de la pestaña (Z = 0, offset = +flangeWidth)
  // Anillo 1: Tope superior de la pestaña (Z = flangeHeight, offset = +flangeWidth)
  // Anillo 2: Pared exterior de la cuchilla (Z = flangeHeight, offset = +bladeThickness)
  // Anillo 3: Filo de corte exterior (Z = bladeHeight, offset = +bladeThickness * 0.5)
  // Anillo 4: Filo de corte interior (Z = bladeHeight, offset = -bladeThickness * 0.5)
  // Anillo 5: Pared interior de la cuchilla (Z = 0, offset = 0)

  // 0. Base pestaña exterior
  for (let i = 0; i < n; i++) {
    const p = scaledPoints[i];
    const norm = normals[i];
    positions.push(p.x + norm.x * flangeWidth, 0, p.y + norm.y * flangeWidth);
  }

  // 1. Tope pestaña exterior
  for (let i = 0; i < n; i++) {
    const p = scaledPoints[i];
    const norm = normals[i];
    positions.push(p.x + norm.x * flangeWidth, flangeHeight, p.y + norm.y * flangeWidth);
  }

  // 2. Unión pestaña a cuchilla exterior
  for (let i = 0; i < n; i++) {
    const p = scaledPoints[i];
    const norm = normals[i];
    positions.push(p.x + norm.x * bladeThickness, flangeHeight, p.y + norm.y * bladeThickness);
  }

  // 3. Filo superior exterior
  for (let i = 0; i < n; i++) {
    const p = scaledPoints[i];
    const norm = normals[i];
    const edgeOffset = params.taperCuttingEdge ? bladeThickness * 0.25 : bladeThickness;
    positions.push(p.x + norm.x * edgeOffset, bladeHeight, p.y + norm.y * edgeOffset);
  }

  // 4. Filo superior interior
  for (let i = 0; i < n; i++) {
    const p = scaledPoints[i];
    positions.push(p.x, bladeHeight, p.y);
  }

  // 5. Pared interior base
  for (let i = 0; i < n; i++) {
    const p = scaledPoints[i];
    positions.push(p.x, 0, p.y);
  }

  // Helper para coser dos anillos contiguos
  const bridgeRings = (ringA: number, ringB: number, reverse = false) => {
    for (let i = 0; i < n; i++) {
      const next = (i + 1) % n;
      const a1 = ringA * n + i;
      const a2 = ringA * n + next;
      const b1 = ringB * n + i;
      const b2 = ringB * n + next;

      if (!reverse) {
        indices.push(a1, b1, a2);
        indices.push(a2, b1, b2);
      } else {
        indices.push(a1, a2, b1);
        indices.push(a2, b2, b1);
      }
    }
  };

  // Coser anillos:
  bridgeRings(0, 1, false);  // Pared vertical pestaña exterior
  bridgeRings(1, 2, false);  // Escala horizontal pestaña superior
  bridgeRings(2, 3, false);  // Pared vertical cuchilla exterior
  bridgeRings(3, 4, false);  // Filo superior de corte
  bridgeRings(4, 5, false);  // Pared interior vertical
  bridgeRings(5, 0, false);  // Fondo base de la pestaña

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
}

/**
 * Genera la geometría 3D del Sello Interior (Stamp) con Relieve y Manija Ergonómica
 */
export function generateStampGeometry(params: CookieCutterParams): THREE.BufferGeometry {
  const {
    shape = 'heart',
    size = 75,
    stampThickness = 2.5,
    stampReliefHeight = 1.8,
    stampHandleHeight = 14,
    stampHandleDiameter = 18,
    stampClearance = 1.2
  } = params;

  const basePoints = getShapePoints(shape, 128);
  const n = basePoints.length;

  // Escalar teniendo en cuenta holgura
  const effectiveSize = Math.max(10, size - stampClearance * 2);
  const scaledPoints: THREE.Vector2[] = basePoints.map(p => new THREE.Vector2(p.x * effectiveSize, p.y * effectiveSize));

  // Crear forma 2D para extrusión
  const shape2D = new THREE.Shape();
  shape2D.moveTo(scaledPoints[0].x, scaledPoints[0].y);
  for (let i = 1; i < n; i++) {
    shape2D.lineTo(scaledPoints[i].x, scaledPoints[i].y);
  }
  shape2D.closePath();

  // 1. Placa base del sello
  const plateGeom = new THREE.ExtrudeGeometry(shape2D, {
    depth: stampThickness,
    bevelEnabled: true,
    bevelSegments: 2,
    steps: 1,
    bevelSize: 0.6,
    bevelThickness: 0.6
  });
  // Rotar para orientar con Z arriba
  plateGeom.rotateX(-Math.PI / 2);

  // 2. Relieve interior decorativo (borde concéntrico en relieve)
  const innerShape = new THREE.Shape();
  const innerScale = 0.82;
  innerShape.moveTo(scaledPoints[0].x * innerScale, scaledPoints[0].y * innerScale);
  for (let i = 1; i < n; i++) {
    innerShape.lineTo(scaledPoints[i].x * innerScale, scaledPoints[i].y * innerScale);
  }
  innerShape.closePath();

  const reliefGeom = new THREE.ExtrudeGeometry(innerShape, {
    depth: stampReliefHeight,
    bevelEnabled: false
  });
  reliefGeom.rotateX(-Math.PI / 2);
  reliefGeom.translate(0, stampThickness, 0);

  // 3. Manija trasera ergonómica (Cilindro con reborde para tirar fácilmente)
  const handleRadius = stampHandleDiameter / 2;
  const handleGeom = new THREE.CylinderGeometry(handleRadius * 1.2, handleRadius * 0.9, stampHandleHeight, 32);
  handleGeom.translate(0, -stampHandleHeight / 2, 0);

  // Unificar geometrías
  // Convertimos a no-indexadas para fusionar fácilmente
  const geomsToMerge = [plateGeom, reliefGeom, handleGeom];
  const combinedPositions: number[] = [];

  geomsToMerge.forEach(g => {
    const nonIndexed = g.toNonIndexed();
    const pos = nonIndexed.getAttribute('position');
    for (let i = 0; i < pos.count; i++) {
      combinedPositions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    }
    g.dispose();
  });

  const finalGeom = new THREE.BufferGeometry();
  finalGeom.setAttribute('position', new THREE.Float32BufferAttribute(combinedPositions, 3));
  finalGeom.computeVertexNormals();

  return finalGeom;
}

/**
 * Exportador directo a formato binario STL para Slicers 3D
 */
export function exportCookieCutterToSTL(geometry: THREE.BufferGeometry, filename = 'cortante-mila.stl') {
  let geom = geometry.clone();
  if (geom.index) {
    geom = geom.toNonIndexed();
  }

  const posAttr = geom.getAttribute('position');
  const trianglesCount = posAttr.count / 3;

  const bufferLength = 80 + 4 + trianglesCount * 50;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const dataView = new DataView(arrayBuffer);

  const header = 'MilaOS 3D Generator - Parametric Cookie Cutter STL';
  for (let i = 0; i < 80; i++) {
    dataView.setUint8(i, i < header.length ? header.charCodeAt(i) : 0);
  }

  dataView.setUint32(80, trianglesCount, true);

  let offset = 84;
  const pA = new THREE.Vector3();
  const pB = new THREE.Vector3();
  const pC = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  for (let i = 0; i < posAttr.count; i += 3) {
    pA.fromBufferAttribute(posAttr, i);
    pB.fromBufferAttribute(posAttr, i + 1);
    pC.fromBufferAttribute(posAttr, i + 2);

    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab).normalize();
    normal.copy(cb);

    dataView.setFloat32(offset, normal.x, true);
    dataView.setFloat32(offset + 4, normal.y, true);
    dataView.setFloat32(offset + 8, normal.z, true);
    offset += 12;

    dataView.setFloat32(offset, pA.x, true);
    dataView.setFloat32(offset + 4, pA.y, true);
    dataView.setFloat32(offset + 8, pA.z, true);
    offset += 12;

    dataView.setFloat32(offset, pB.x, true);
    dataView.setFloat32(offset + 4, pB.y, true);
    dataView.setFloat32(offset + 8, pB.z, true);
    offset += 12;

    dataView.setFloat32(offset, pC.x, true);
    dataView.setFloat32(offset + 4, pC.y, true);
    dataView.setFloat32(offset + 8, pC.z, true);
    offset += 12;

    dataView.setUint16(offset, 0, true);
    offset += 2;
  }

  const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename.endsWith('.stl') ? filename : `${filename}.stl`;
  link.click();
  URL.revokeObjectURL(link.href);
}
